import { KeyboardEvent, MouseEvent, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AppDataContext } from "modules/main/context/appData";
import { useFetch } from "modules/main/hooks/useFetch";
import { storePreferencesEndPoint } from "modules/main/services/endpoints";
import { getStoreBdData } from "utils/storeDataManager";
import {
  readOnboardingState,
  writeOnboardingState,
  type OnboardingState,
} from "../types/onboarding-state";

const MAX_VISIBLE_STORES = 4;

interface MembershipItem {
  /**
   * Numeric `suscriptions.id` FK exposed by the public store preferences
   * endpoint. This is the canonical identifier the backend persists into
   * `user_suscriptions.suscription_id` — see `OnboardingRequest.subscription_ids:
   * List[int]`. The legacy `text_key` field below stays as the human-readable
   * label for the UI; the numeric id is what `subscription_ids` actually
   * carries to the backend.
   */
  id: number;
  text_key: string;
  logo_url: string;
  delivery_cost: number;
  description: string;
}

interface StorePreference {
  id: number;
  name: string;
  logoUrl: string;
  enabled: boolean;
  minPurchaseAmount: number;
  freeDelivery: number | null;
  deliveryCost: number;
  memberships: MembershipItem[];
}

interface StorePreferenceOption {
  storeId: number;
  name: string;
  logoUrl: string;
}

interface OnboardingStorePreferencesProps {
  /** Persisted location id selected in onboarding step two. */
  locationId: number;
}

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

const isMembershipControl = (target: EventTarget | null) => {
  return target instanceof HTMLElement && target.closest('[data-membership-control="true"]') !== null;
};

/**
 * Existing-location onboarding supermarket preference controls.
 *
 * Fetches public store preferences for the provided location, joins them with
 * the global store catalog, and keeps include, membership, and favorite-store
 * choices as local UI state.
 *
 * Persistence (cut-2 bugfix): the user's include and membership selections
 * are mirrored into the persisted onboarding draft
 * (`localStorage["carriapp:onboarding"]`) so they reach the
 * `POST /api/users/onboarding` request on the next `/store` visit. The
 * component hydrates the initial state from the persisted draft on mount
 * (so a remount at step 3 resumes with the previously-chosen selections)
 * and writes back on every toggle. The favorite-store selection
 * (``favoriteStoreId``) is now ALSO persisted as ``habitual_store_id``
 * — the numeric `StorePreference.id` the user selected in the
 * "¿Donde compras siempre?" dropdown — so the backend receives the
 * explicit habitual store instead of inferring it from the first
 * enabled ``store_ids`` entry.
 */
const OnboardingStorePreferences = ({ locationId }: OnboardingStorePreferencesProps) => {
  const { storeList } = useContext(AppDataContext);
  const requestUrl = storePreferencesEndPoint(false, locationId);
  const { data, error, loading } = useFetch<StorePreference[]>(requestUrl);
  const [includedByStoreId, setIncludedByStoreId] = useState<Record<number, boolean>>({});
  const [membershipByStoreId, setMembershipByStoreId] = useState<Record<string, boolean>>({});
  const [favoriteStoreId, setFavoriteStoreId] = useState<string>("");
  const hasHydratedPersistedDraftRef = useRef(false);

  const options = useMemo<StorePreferenceOption[]>(() => {
    return (data ?? []).slice(0, MAX_VISIBLE_STORES).map((preference) => {
      const catalogStore = getStoreBdData(preference.id, storeList);

      return {
        storeId: preference.id,
        name: catalogStore.name || preference.name,
        logoUrl: catalogStore.logo_url || preference.logoUrl || "",
      };
    });
  }, [data, storeList]);

  const membershipOptions = useMemo<Record<number, MembershipItem[]>>(() => {
    return (data ?? []).slice(0, MAX_VISIBLE_STORES).reduce<Record<number, MembershipItem[]>>(
      (acc, preference) => {
        acc[preference.id] = preference.memberships ?? [];
        return acc;
      },
      {},
    );
  }, [data]);

  /** Hydrate persisted selections once the async catalog has resolved. */
  useEffect(() => {
    if (hasHydratedPersistedDraftRef.current || options.length === 0) {
      return;
    }

    const persisted = readOnboardingState();
    hasHydratedPersistedDraftRef.current = true;

    if (!persisted) {
      return;
    }

    if (persisted.store_ids.length > 0) {
      setIncludedByStoreId((current) => {
        const next: Record<number, boolean> = { ...current };
        for (const storeId of options.map((o) => o.storeId)) {
          next[storeId] = persisted.store_ids.includes(storeId);
        }
        return next;
      });
    }

    if (persisted.subscription_ids.length > 0) {
      const persistedIdSet = new Set(persisted.subscription_ids);
      setMembershipByStoreId((current) => {
        const next: Record<string, boolean> = { ...current };
        for (const option of options) {
          const storeMemberships = membershipOptions[option.storeId] ?? [];
          for (const membership of storeMemberships) {
            const key = `${option.storeId}:${membership.text_key}`;
            next[key] = persistedIdSet.has(membership.id);
          }
        }
        return next;
      });
    }

    if (persisted.habitual_store_id != null && persisted.habitual_store_id > 0) {
      setFavoriteStoreId(String(persisted.habitual_store_id));
    }
  }, [membershipOptions, options]);

  useEffect(() => {
    if (options.length === 0) {
      return;
    }

    setIncludedByStoreId((current) =>
      options.reduce<Record<number, boolean>>((next, option) => {
        next[option.storeId] = current[option.storeId] ?? false;
        return next;
      }, {}),
    );
  }, [options]);

  const persistSelections = (
    nextIncludedByStoreId: Record<number, boolean>,
    nextMembershipByStoreId: Record<string, boolean>,
    nextFavoriteStoreId: string,
  ) => {
    if (options.length === 0) {
      return;
    }

    const includedStoreIds = options
      .filter((option) => nextIncludedByStoreId[option.storeId] ?? false)
      .map((option) => option.storeId);
    const includedStoreIdSet = new Set(includedStoreIds);
    const selectedMembershipIds: number[] = [];

    for (const option of options) {
      if (!includedStoreIdSet.has(option.storeId)) {
        continue;
      }
      const storeMemberships = membershipOptions[option.storeId] ?? [];
      for (const membership of storeMemberships) {
        const key = `${option.storeId}:${membership.text_key}`;
        if (
          nextMembershipByStoreId[key] === true &&
          Number.isInteger(membership.id) &&
          membership.id > 0
        ) {
          selectedMembershipIds.push(membership.id);
        }
      }
    }

    const parsedHabitual = nextFavoriteStoreId !== ""
      ? parseInt(nextFavoriteStoreId, 10) || null
      : null;

    const existing = readOnboardingState();
    const baseDraft: OnboardingState = existing ?? {
      currentStep: 3,
      city_id: null,
      city_name: null,
      location_id: locationId,
      location_name: null,
      isFallback: false,
      purchase_frequency: null,
      spend_per_purchase: null,
      store_ids: [],
      subscription_ids: [],
      habitual_store_id: null,
    };

    writeOnboardingState({
      ...baseDraft,
      store_ids: includedStoreIds,
      subscription_ids: selectedMembershipIds,
      habitual_store_id: parsedHabitual,
    });
  };

  const toggleIncludedStore = (storeId: number) => {
    setIncludedByStoreId((current) => {
      const next = {
        ...current,
        [storeId]: !(current[storeId] ?? false),
      };
      persistSelections(next, membershipByStoreId, favoriteStoreId);
      return next;
    });
  };

  const handleCardClick = (event: MouseEvent<HTMLDivElement>, storeId: number) => {
    if (isMembershipControl(event.target)) {
      return;
    }

    toggleIncludedStore(storeId);
  };

  const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>, storeId: number) => {
    if (event.target !== event.currentTarget) {
      return;
    }

    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      toggleIncludedStore(storeId);
    }
  };

  const stopMembershipPropagation = (event: MouseEvent | KeyboardEvent) => {
    event.stopPropagation();
  };

  const setMembershipSelection = (
    storeId: number,
    membershipKey: string,
    isSelected: boolean,
  ) => {
    setMembershipByStoreId((currentMemberships) => {
      const nextMemberships = {
        ...currentMemberships,
        [membershipKey]: isSelected,
      };

      const nextIncludedStores = isSelected
        ? {
            ...includedByStoreId,
            [storeId]: true,
          }
        : includedByStoreId;

      if (isSelected) {
        setIncludedByStoreId(nextIncludedStores);
      }

      persistSelections(nextIncludedStores, nextMemberships, favoriteStoreId);
      return nextMemberships;
    });
  };

  const toggleMembershipSelection = (storeId: number, membershipKey: string) => {
    const nextSelected = !(membershipByStoreId[membershipKey] ?? false);
    setMembershipSelection(storeId, membershipKey, nextSelected);
  };

  /** Keeps the persisted draft in sync with the default loaded catalog. */
  useEffect(() => {
    if (options.length === 0) {
      return;
    }
    persistSelections(includedByStoreId, membershipByStoreId, favoriteStoreId);
  }, [
    options,
    membershipOptions,
    includedByStoreId,
    membershipByStoreId,
    favoriteStoreId,
  ]);

  const includedCount = options.filter(
    (option) => includedByStoreId[option.storeId] ?? false,
  ).length;

  if (loading) {
    return <p className="text-sm text-muted-foreground">Cargando supermercados...</p>;
  }

  if (error) {
    return <p className="text-sm text-muted-foreground">No pudimos cargar supermercados para este sector.</p>;
  }

  if (options.length === 0) {
    return <p className="text-sm text-muted-foreground">No hay supermercados disponibles para este sector.</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {options.map((option) => {
          const includeLabel = `Incluir ${option.name}`;
          const storeMemberships = membershipOptions[option.storeId] ?? [];
          const isIncluded = includedByStoreId[option.storeId] ?? false;

          return (
            <Card
              key={option.storeId}
              aria-checked={isIncluded}
              aria-label={includeLabel}
              className={cn(
                "cursor-pointer py-4 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                isIncluded && "border-primary bg-primary/5",
                !isIncluded && "border-dashed bg-muted/40 opacity-60 grayscale",
              )}
              data-testid="onboarding-store-card"
              onClick={(event) => handleCardClick(event, option.storeId)}
              onKeyDown={(event) => handleCardKeyDown(event, option.storeId)}
              role="checkbox"
              tabIndex={0}
            >
              <CardContent className="flex flex-col gap-4 px-4">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    {option.logoUrl ? (
                      <AvatarImage src={option.logoUrl} alt={option.name} />
                    ) : null}
                    <AvatarFallback>{getInitials(option.name)}</AvatarFallback>
                  </Avatar>
                  <span className="font-medium text-foreground">{option.name}</span>
                </div>
                {storeMemberships.length > 0 && (
                  <div className="flex flex-col gap-2 text-sm">
                    {storeMemberships.map((membership) => {
                      const membershipKey = `${option.storeId}:${membership.text_key}`;
                      return (
                        <div
                          key={membershipKey}
                          className="flex items-center justify-between gap-3 cursor-pointer"
                          data-membership-control="true"
                          onClick={(event) => {
                            stopMembershipPropagation(event);
                            toggleMembershipSelection(option.storeId, membershipKey);
                          }}
                          onKeyDown={(event) => {
                            stopMembershipPropagation(event);
                            if (event.key === " " || event.key === "Enter") {
                              event.preventDefault();
                              toggleMembershipSelection(option.storeId, membershipKey);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <span className="flex items-center gap-2 flex-1 min-w-0">
                            {membership.logo_url && (
                              <img
                                src={membership.logo_url}
                                alt={membership.text_key}
                                className="h-5 w-5 rounded object-contain shrink-0"
                              />
                            )}
                            <span className="text-xs text-muted-foreground truncate">
                              {membership.description || membership.text_key}
                            </span>
                          </span>
                          <Checkbox
                            aria-label={membership.description || membership.text_key}
                            checked={membershipByStoreId[membershipKey] ?? false}
                            onClick={stopMembershipPropagation}
                            onCheckedChange={(checked) => {
                              setMembershipSelection(
                                option.storeId,
                                membershipKey,
                                checked === true,
                              );
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {includedCount === 1 ? (
        <div
          className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100"
          role="status"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>Te recomendamos al menos 2?</span>
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium" id="favorite-store-label">
          ¿Dónde compras siempre?
        </span>
        <Select
          value={favoriteStoreId}
          onValueChange={(nextFavoriteStoreId) => {
            setFavoriteStoreId(nextFavoriteStoreId);
            persistSelections(
              includedByStoreId,
              membershipByStoreId,
              nextFavoriteStoreId,
            );
          }}
        >
          <SelectTrigger aria-labelledby="favorite-store-label">
            <SelectValue placeholder="Selecciona un supermercado" />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.storeId} value={String(option.storeId)}>
                {option.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default OnboardingStorePreferences;
